/**
 * AdminAssets.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import HelpPopup from './HelpPopup';
import ProductPickerModal from './ProductPickerModal';
import { useLocation, useNavigate } from 'react-router-dom';
import './AdminAssets.css';
import { parseMultipleTunes, mapTunesToBulkImportEntries } from '../utils/abcBulkImport';
import { parseContentDispositionFilename } from '../utils/contentDispositionFilename';

/**
 * Prefer public URL path /assets/abc/… for ABC tunes when DB still has uploads/multer or absolute paths.
 * Raw value remains available via tooltip when it differs.
 */
function displayAssetFilePath(asset) {
  if (!asset || !asset.file_path) return asset?.file_path || '';
  const raw = String(asset.file_path);
  if (raw.startsWith('/assets/')) return raw;
  if (asset.category === 'ABC-Tune' && asset.original_filename && /\.abc$/i.test(asset.original_filename)) {
    const base = asset.original_filename.split(/[/\\]/).pop();
    const fn = asset.filename || '';
    const multerish =
      /^file-\d{10,}-[a-z0-9]+\.abc$/i.test(fn) ||
      /uploads[/\\]file-\d+/i.test(raw) ||
      /[/\\]backend[/\\]uploads[/\\]/i.test(raw) ||
      /^[/\\]?([A-Za-z]:)?[/\\]Users[/\\]/i.test(raw) ||
      /^[/\\]?([A-Za-z]:)?[/\\]home[/\\]/i.test(raw);
    if (multerish && base) return `/assets/abc/${base}`;
  }
  return raw;
}

function cleanAssetFilenameHint(name) {
  if (!name || typeof name !== 'string') return '';
  let s = name.replace(/[\u202f\u00a0\u2007\u2009\u200a\ufeff]/g, ' ');
  try {
    s = s.normalize('NFKC');
  } catch (_) { /* ignore */ }
  const base = s.split(/[/\\]/).pop() || '';
  return base.replace(/[\x00-\x1f]/g, '_');
}

const AdminAssets = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [filters, setFilters] = useState({
    file_type: '',
    category: '',
    search: '',
    tags: '',
    file_size: '',
    show_inactive: false
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [categories, setCategories] = useState([]);
  const [, setTags] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportTunes, setBulkImportTunes] = useState([]);
  const [bulkImportHeader, setBulkImportHeader] = useState('');
  const [bulkImportProgress, setBulkImportProgress] = useState({ current: 0, total: 0, status: '' });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newFile, setNewFile] = useState(null);
  const fileInputRef = useRef(null);
  const bulkImportFileInputRef = useRef(null);
  const [abcFileContent, setAbcFileContent] = useState(null);
  const [uploadStoredFilename, setUploadStoredFilename] = useState('');
  const [editStoredFilename, setEditStoredFilename] = useState('');

  // ABC-Tune specific fields state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [abcFields, setAbcFields] = useState({
    rhythm_style: '',
    key: '',
    composer_arranger: '',
    origin_region: '',
    source_album: '',
    tune_title: ''
  });
  const [tagsValue, setTagsValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productIdsInput, setProductIdsInput] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [isPublic, setIsPublic] = useState(true);

  // Duplicate checking states
  const [activeTab, setActiveTab] = useState('assets'); // 'assets' or 'duplicates'
  const [duplicateData, setDuplicateData] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveAssets, setInactiveAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false); // New state for selecting all matching across pages
  const [deleteFilesOnCleanup, setDeleteFilesOnCleanup] = useState(false);
  const [autoDeduping, setAutoDeduping] = useState(false);
  const [autoDedupeResult, setAutoDedupeResult] = useState(null);

  // Import/Export (hidden file inputs for CSV / ZIP)
  const importFileInputRef = useRef(null);
  const importZipFileInputRef = useRef(null);

  // Check permissions
  const canManageAssets = currentUser && currentUser.roles &&
    (currentUser.roles.includes('admin') || currentUser.roles.includes('super_admin'));

  const openUploadModalWithBulkTune = useCallback((tune) => {
    if (!tune || tune.fullContent == null) return;
    setShowBulkImportModal(false);
    const blob = new Blob([tune.fullContent], { type: 'text/plain' });
    const file = new File([blob], tune.filename, { type: 'text/plain' });
    setNewFile(file);
    setSelectedCategory('ABC-Tune');
    setAbcFields({
      rhythm_style: tune.metadata?.rhythm_style || '',
      key: tune.metadata?.key_signature || tune.metadata?.key || '',
      composer_arranger: tune.metadata?.composer_arranger || '',
      origin_region: tune.metadata?.origin_region || '',
      source_album: tune.metadata?.source_album || '',
      tune_title: tune.metadata?.tune_title || ''
    });
    setTagsValue(tune.tags || '');
    const cleanDescription = (tune.description || '').replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim();
    setDescriptionValue(cleanDescription);
    setAbcFileContent(tune.fullContent);
    setShowUploadModal(true);
  }, []);

  useEffect(() => {
    const pending = location.state && location.state.pendingAbcBulkTune;
    if (!pending || !canManageAssets) return;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
    openUploadModalWithBulkTune(pending);
  }, [location.state, location.pathname, location.search, canManageAssets, navigate, openUploadModalWithBulkTune]);

  useEffect(() => {
    // Parse URL search parameters on load
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get('search');

    if (searchQuery) {
      setFilters(prev => ({ ...prev, search: searchQuery }));
    }
  }, [location.search]);

  // Deep-link from tune metadata (and product page): /admin/assets?assetId=123
  useEffect(() => {
    if (!canManageAssets) return;
    const raw = new URLSearchParams(location.search).get('assetId');
    if (!raw) return;
    const id = parseInt(raw, 10);
    if (!Number.isFinite(id) || id <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/assets/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok || cancelled) return;
        const asset = await res.json();
        if (!cancelled && asset && asset.id != null) setEditingAsset(asset);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, canManageAssets]);

  useEffect(() => {
    if (editingAsset) {
      setEditStoredFilename(editingAsset.filename || editingAsset.original_filename || '');
    } else {
      setEditStoredFilename('');
    }
  }, [editingAsset]);

  useEffect(() => {
    if (canManageAssets) {
      fetchAssets();
      fetchCategories();
      fetchTags();
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageAssets, pagination?.page, filters]);

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(newLimit),
      page: 1
    }));
    // When limit changes, reset isAllSelected
    setIsAllSelected(false);
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination?.page || 1,
        limit: pagination?.limit || 20,
        ...filters,
        t: new Date().getTime() // Cache busting parameter
      });

      const response = await fetch(`/api/assets?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch assets');

      const data = await response.json();
      setAssets(data.assets || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (error) {
      console.error('Error fetching assets:', error);
      showError('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/assets/categories/list');
      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch('/api/assets/tags/list');
      if (response.ok) {
        const data = await response.json();
        setTags(Array.isArray(data) ? data : []);
      } else {
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  const [products, setProducts] = useState([]);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products');
      if (response.ok) {
        const data = await response.json();
        const productsList = Array.isArray(data) ? data : (data.products || []);
        setProducts(productsList);
        // Update selected products display when products are fetched
        if (selectedProductIds.length > 0) {
          const selected = productsList.filter(p => selectedProductIds.includes(p.id));
          setSelectedProducts(selected);
          setProductIdsInput(selectedProductIds.join(', '));
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleProductIdsChange = (value) => {
    setProductIdsInput(value);
    // Parse comma-separated IDs
    const ids = value.split(',')
      .map(id => id.trim())
      .filter(id => id && !isNaN(id))
      .map(id => parseInt(id));
    setSelectedProductIds(ids);

    // Update selected products display
    if (products.length > 0) {
      const selected = products.filter(p => ids.includes(p.id));
      setSelectedProducts(selected);
    }
  };

  const handleProductPickerSelect = (product) => {
    if (!selectedProductIds.includes(product.id)) {
      const newIds = [...selectedProductIds, product.id];
      setSelectedProductIds(newIds);
      setProductIdsInput(newIds.join(', '));
      setSelectedProducts([...selectedProducts, product]);
    }
    setShowProductPicker(false);
  };

  const removeProduct = (productId) => {
    const newIds = selectedProductIds.filter(id => id !== productId);
    setSelectedProductIds(newIds);
    setProductIdsInput(newIds.join(', '));
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    setUploading(true);

    try {
      const formData = new FormData(event.target);

      // If file was set programmatically (from bulk import), use it instead of form file
      if (newFile) {
        formData.set('file', newFile);
      }

      // If ABC-Tune category, add ABC metadata to description as JSON
      if (selectedCategory === 'ABC-Tune') {
        const abcMetadata = {
          rhythm_style: abcFields.rhythm_style,
          key: abcFields.key,
          composer_arranger: abcFields.composer_arranger,
          origin_region: abcFields.origin_region,
          source_album: abcFields.source_album,
          tune_title: abcFields.tune_title
        };

        // Merge ABC metadata with existing description
        const userDescription = descriptionValue || formData.get('description') || '';
        const descriptionWithMetadata = userDescription
          ? `${userDescription}\n\n[ABC_METADATA]${JSON.stringify(abcMetadata)}[/ABC_METADATA]`
          : `[ABC_METADATA]${JSON.stringify(abcMetadata)}[/ABC_METADATA]`;

        formData.set('description', descriptionWithMetadata);

        // Use tagsValue if available, otherwise use formData tags
        const tagsToUse = tagsValue || formData.get('tags') || '';
        if (tagsToUse) {
          formData.set('tags', tagsToUse);
        }
      } else if (tagsValue) {
        // If not ABC-Tune but tagsValue is set, use it
        formData.set('tags', tagsValue);
      }

      // Add product IDs if selected
      if (selectedProductIds.length > 0) {
        formData.append('product_ids', JSON.stringify(selectedProductIds));
      }

      const ufn = (uploadStoredFilename || '').trim();
      if (ufn) {
        formData.set('stored_filename', ufn);
      }

      const response = await fetch('/api/assets', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        console.error('Upload failed:', response.status, errorData);
        throw new Error(errorData.error || errorData.errors?.[0]?.msg || 'Upload failed');
      }

      const result = await response.json();
      console.log('Upload success:', result);
      showSuccess('Asset uploaded successfully');
      setShowUploadModal(false);
      setSelectedCategory('');
      setAbcFields({
        rhythm_style: '',
        key: '',
        composer_arranger: '',
        origin_region: '',
        source_album: '',
        tune_title: ''
      });
      setTagsValue('');
      setDescriptionValue('');
      setAbcFileContent(null);
      setNewFile(null); // Clear programmatically set file
      setSelectedProductIds([]);
      setProductIdsInput('');
      setSelectedProducts([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadStoredFilename('');
      fetchAssets();
      event.target.reset();
    } catch (error) {
      console.error('Upload error:', error);
      showError(error.message || 'Failed to upload asset');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();

    if (!newCategoryName.trim()) {
      showError('Category name is required');
      return;
    }

    try {
      const response = await fetch('/api/assets/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newCategoryName.trim(),
          description: newCategoryDescription.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create category' }));
        throw new Error(errorData.error || 'Failed to create category');
      }

      showSuccess('Category created successfully');
      setShowCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      fetchCategories();
    } catch (error) {
      console.error('Create category error:', error);
      showError(error.message || 'Failed to create category');
    }
  };

  // Export assets to CSV
  const downloadCSV = async () => {
    try {
      const response = await fetch('/api/assets/export/csv', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'assets_catalog.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccess('Assets exported successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      showError('Failed to download CSV. Please try again.');
    }
  };

  // Import modal handlers
  const handleImportClick = () => {
    importFileInputRef.current?.click();
  };

  // Import assets from CSV
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvData = event.target.result;
        const response = await fetch('/api/assets/import/csv', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            csvData: csvData,
            mode: 'update'
          })
        });

        const result = await response.json();

        if (response.ok) {
          showSuccess(`CSV import completed successfully! Updated ${result.imported} assets.`);
          // Refresh assets after upload with cache busting
          fetchAssets();
        } else {
          showError(`CSV import failed: ${result.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error uploading CSV:', error);
        showError('Failed to upload CSV. Please try again.');
      }
    };

    reader.readAsText(file);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const response = await fetch(`/api/assets/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Delete failed');

      showSuccess('Asset deleted successfully');
      fetchAssets();
    } catch (error) {
      console.error('Delete error:', error);
      showError('Failed to delete asset');
    }
  };

  const handleDownload = (asset) => {
    window.open(`/api/assets/${asset.id}/download`, '_blank');
  };

  const handleEdit = async (asset) => {
    setEditingAsset(asset);
    setNewFile(null);

    // Set category for ABC fields
    setSelectedCategory(asset.category || '');

    // Set tags value
    setTagsValue(asset.tags || '');

    // Set description value (remove ABC metadata for display)
    const cleanDescription = asset.description ? asset.description.replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim() : '';
    setDescriptionValue(cleanDescription);

    // Set active/public state
    setIsActive(!!asset.is_active);
    setIsPublic(!!asset.is_public);

    // Fetch product IDs for this asset
    try {
      const productResponse = await fetch(`/api/assets/${asset.id}/products`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (productResponse.ok) {
        const productData = await productResponse.json();
        const productIds = Array.isArray(productData)
          ? productData.map(p => p.id || p.product_id)
          : (productData.product_ids || []);
        setSelectedProductIds(productIds);
        setProductIdsInput(productIds.join(', '));
        // Update selected products display
        if (products.length > 0) {
          const selected = products.filter(p => productIds.includes(p.id));
          setSelectedProducts(selected);
        }
      } else {
        setSelectedProductIds([]);
        setProductIdsInput('');
        setSelectedProducts([]);
      }
    } catch (error) {
      console.error('Error fetching asset products:', error);
      setSelectedProductIds([]);
      setProductIdsInput('');
      setSelectedProducts([]);
    }

    // Parse ABC metadata from description if it exists
    if (asset.category === 'ABC-Tune' && asset.description) {
      const abcMatch = asset.description.match(/\[ABC_METADATA\](.*?)\[\/ABC_METADATA\]/s);
      if (abcMatch) {
        try {
          const abcMetadata = JSON.parse(abcMatch[1]);
          setAbcFields({
            rhythm_style: abcMetadata.rhythm_style || '',
            key: abcMetadata.key || '',
            composer_arranger: abcMetadata.composer_arranger || '',
            origin_region: abcMetadata.origin_region || '',
            source_album: abcMetadata.source_album || '',
            tune_title: abcMetadata.tune_title || ''
          });
        } catch (e) {
          console.error('Error parsing ABC metadata:', e);
          setAbcFields({
            rhythm_style: '',
            key: '',
            composer_arranger: '',
            origin_region: '',
            source_album: '',
            tune_title: ''
          });
        }
      } else {
        setAbcFields({
          rhythm_style: '',
          key: '',
          composer_arranger: '',
          origin_region: '',
          source_album: '',
          tune_title: ''
        });
      }
    } else {
      setAbcFields({
        rhythm_style: '',
        key: '',
        composer_arranger: '',
        origin_region: '',
        source_album: '',
        tune_title: ''
      });
    }
  };

  const handleViewLink = (asset) => {
    // Use relative URLs for Docker/production compatibility
    const thumbnailUrl = `/api/assets/${asset.id}/thumbnail`;
    const downloadUrl = `/api/assets/${asset.id}/download`;

    // Create a temporary modal to show links with copy buttons
    const modalHtml = `
      <div class="asset-link-modal-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div class="asset-link-modal" style="background: var(--color-background); border-radius: 12px; max-width: 600px; width: 100%; max-height: 80vh; overflow-y: auto; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
          <div class="modal-header" style="padding: 20px; border-bottom: 1px solid var(--color-border); display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; color: var(--color-text-primary); font-size: 1.3rem;">🔗 Asset Links</h3>
            <button onclick="this.closest('.asset-link-modal-overlay').remove()" style="background: none; border: none; font-size: 24px; cursor: pointer; color: var(--color-text-secondary); padding: 0; width: 30px; height: 30px;">×</button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div style="margin-bottom: 20px;">
              <p style="margin: 0 0 10px 0; color: var(--color-text-primary); font-weight: 600;">📄 Filename:</p>
              <div style="background: var(--color-background-light); padding: 10px; border-radius: 6px; font-family: monospace; color: var(--color-text-primary); word-break: break-all;">${asset.original_filename}</div>
            </div>
            
            <div style="margin-bottom: 20px;">
              <p style="margin: 0 0 10px 0; color: var(--color-text-primary); font-weight: 600;">📸 Thumbnail URL (for images):</p>
              <div style="display: flex; gap: 8px;">
                <input type="text" value="${thumbnailUrl}" readonly style="flex: 1; padding: 10px; border: 1px solid var(--color-border); border-radius: 6px; font-family: monospace; background: var(--color-background); color: var(--color-text-primary);" onclick="this.select()" />
                <button onclick="navigator.clipboard.writeText('${thumbnailUrl}').then(() => alert('✓ Thumbnail URL copied!')).catch(() => alert('Failed to copy'))" style="padding: 10px 20px; background: #17a2b8; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; white-space: nowrap;">📋 Copy</button>
              </div>
            </div>
            
            <div style="margin-bottom: 20px;">
              <p style="margin: 0 0 10px 0; color: var(--color-text-primary); font-weight: 600;">📥 Download URL (for files):</p>
              <div style="display: flex; gap: 8px;">
                <input type="text" value="${downloadUrl}" readonly style="flex: 1; padding: 10px; border: 1px solid var(--color-border); border-radius: 6px; font-family: monospace; background: var(--color-background); color: var(--color-text-primary);" onclick="this.select()" />
                <button onclick="navigator.clipboard.writeText('${downloadUrl}').then(() => alert('✓ Download URL copied!')).catch(() => alert('Failed to copy'))" style="padding: 10px 20px; background: #17a2b8; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; white-space: nowrap;">📋 Copy</button>
              </div>
            </div>
            
            <div style="margin-bottom: 20px;">
              <p style="margin: 0 0 10px 0; color: var(--color-text-primary); font-weight: 600;">💡 HTML Image Tag:</p>
              <div style="display: flex; gap: 8px;">
                <textarea readonly style="flex: 1; padding: 10px; border: 1px solid var(--color-border); border-radius: 6px; font-family: monospace; background: var(--color-background); color: var(--color-text-primary); min-height: 60px; resize: vertical;" onclick="this.select()"><img src="${thumbnailUrl}" alt="${asset.original_filename}" style="max-width:100%;" /></textarea>
                <button onclick="navigator.clipboard.writeText('<img src=\\"${thumbnailUrl}\\" alt=\\"${asset.original_filename}\\" style=\\"max-width:100%;\\" />').then(() => alert('✓ HTML copied!')).catch(() => alert('Failed to copy'))" style="padding: 10px 20px; background: #17a2b8; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; white-space: nowrap;">📋 Copy</button>
              </div>
            </div>
            
            <div style="margin-bottom: 20px;">
              <p style="margin: 0 0 10px 0; color: var(--color-text-primary); font-weight: 600;">🔗 HTML Link Tag:</p>
              <div style="display: flex; gap: 8px;">
                <textarea readonly style="flex: 1; padding: 10px; border: 1px solid var(--color-border); border-radius: 6px; font-family: monospace; background: var(--color-background); color: var(--color-text-primary); min-height: 60px; resize: vertical;" onclick="this.select()"><a href="${downloadUrl}" target="_blank">${asset.original_filename}</a></textarea>
                <button onclick="navigator.clipboard.writeText('<a href=\\"${downloadUrl}\\" target=\\"_blank\\">${asset.original_filename}</a>').then(() => alert('✓ HTML copied!')).catch(() => alert('Failed to copy'))" style="padding: 10px 20px; background: #17a2b8; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; white-space: nowrap;">📋 Copy</button>
              </div>
            </div>
            
            <div style="display: flex; gap: 10px; padding-top: 10px; border-top: 1px solid var(--color-border);">
              <button onclick="window.open('${downloadUrl}', '_blank')" style="flex: 1; padding: 12px 20px; background: #28a745; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px;">👁️ View in New Tab</button>
              <button onclick="this.closest('.asset-link-modal-overlay').remove()" style="flex: 1; padding: 12px 20px; background: #6c757d; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px;">Close</button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert modal into DOM
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = modalHtml;
    document.body.appendChild(modalContainer.firstElementChild);
  };

  const handleUpdateAsset = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const asOptionalString = (value) => (value == null ? '' : String(value));
    const formatApiError = (errorData, fallback) => {
      if (!errorData) return fallback;
      if (errorData.error) return errorData.error;
      if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        return errorData.errors.map((e) => `${e.path}: ${e.msg}`).join('; ');
      }
      return fallback;
    };

    const updateData = {
      category: asOptionalString(selectedCategory),
      subcategory: asOptionalString(formData.get('subcategory') || editingAsset.subcategory),
      description: asOptionalString(descriptionValue),
      alt_text: asOptionalString(formData.get('alt_text') || editingAsset.alt_text),
      tags: asOptionalString(tagsValue),
      is_public: isPublic,
      is_active: isActive,
      product_ids: selectedProductIds,
      stored_filename: (editStoredFilename || '').trim()
    };

    // If ABC-Tune category, add ABC metadata to description as JSON
    if (selectedCategory === 'ABC-Tune') {
      const abcMetadata = {
        rhythm_style: abcFields.rhythm_style,
        key: abcFields.key,
        composer_arranger: abcFields.composer_arranger,
        origin_region: abcFields.origin_region,
        source_album: abcFields.source_album,
        tune_title: abcFields.tune_title
      };

      // Remove existing ABC metadata from description if present
      let cleanDescription = descriptionValue || updateData.description || '';
      cleanDescription = cleanDescription.replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim();

      // Merge ABC metadata with existing description
      const descriptionWithMetadata = cleanDescription
        ? `${cleanDescription}\n\n[ABC_METADATA]${JSON.stringify(abcMetadata)}[/ABC_METADATA]`
        : `[ABC_METADATA]${JSON.stringify(abcMetadata)}[/ABC_METADATA]`;

      updateData.description = descriptionWithMetadata;

      // Use tagsValue if available
      if (tagsValue) {
        updateData.tags = tagsValue;
      }
    } else if (tagsValue) {
      // If not ABC-Tune but tagsValue is set, use it
      updateData.tags = tagsValue;
    }

    // Add product IDs if selected
    if (selectedProductIds.length > 0) {
      updateData.product_ids = selectedProductIds;
    } else {
      updateData.product_ids = [];
    }

    try {
      // If a new file is selected, replace the file first
      if (newFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', newFile);

        const fileResponse = await fetch(`/api/assets/${editingAsset.id}/file`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: fileFormData
        });

        if (!fileResponse.ok) {
          const errorData = await fileResponse.json();
          throw new Error(formatApiError(errorData, 'File replacement failed'));
        }
      }

      // Update metadata
      const response = await fetch(`/api/assets/${editingAsset.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(formatApiError(errorData, 'Update failed'));
      }

      showSuccess('Asset updated successfully');
      setEditingAsset(null);
      setNewFile(null);
      setSelectedCategory('');
      setAbcFields({
        rhythm_style: '',
        key: '',
        composer_arranger: '',
        origin_region: '',
        source_album: '',
        tune_title: ''
      });
      setTagsValue('');
      setDescriptionValue('');
      setAbcFileContent(null);
      setSelectedProductIds([]);
      setProductIdsInput('');
      setSelectedProducts([]);
      fetchAssets();
    } catch (error) {
      console.error('Update error:', error);
      showError(`Failed to update asset: ${error.message}`);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    setIsAllSelected(false); // Reset selection on filter change
  };

  // Parse ABC file content
  const parseABCFile = (content) => {
    const lines = content.split('\n');
    const fields = {};

    // Parse ABC notation fields
    // ABC format: Field:Value (e.g., T:Big Mike's Jig)
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('%')) continue; // Skip comments and empty lines

      // Match ABC field format: X:value
      const match = trimmed.match(/^([A-Za-z]):(.*)$/);
      if (match) {
        const field = match[1].toUpperCase();
        const value = match[2].trim();

        if (!fields[field]) {
          fields[field] = value;
        } else if (Array.isArray(fields[field])) {
          fields[field].push(value);
        } else {
          // If field already exists, convert to array
          fields[field] = [fields[field], value];
        }
      }
    }

    return fields;
  };

  // Auto-populate all ABC fields from file
  const handleAutoPopulateFromFile = async () => {
    if (!abcFileContent) {
      showError('Please select an ABC file first');
      return;
    }

    try {
      const fields = parseABCFile(abcFileContent);

      // Populate ABC fields
      setAbcFields({
        rhythm_style: fields.R || '',
        key: fields.K || '',
        composer_arranger: [fields.C, fields.Z].filter(Boolean).join(', ') || '',
        origin_region: [fields.O, fields.A].filter(Boolean).join(', ') || '',
        source_album: [fields.D, fields.B].filter(Boolean).join(', ') || '',
        tune_title: fields.T || ''
      });

      // Auto-populate tags in the format: ABC,jig, key A,Kinnon Beaton,Cape Breton,Andrea Beaton, The Tap Session, The Beaton Collection
      // Filter out URLs from composer/arranger (Z: field often contains URLs)
      const composer = fields.C || '';
      const arranger = fields.Z ? fields.Z.replace(/https?:\/\/[^\s,]+/gi, '').trim() : '';
      const composerArranger = [composer, arranger].filter(Boolean).join(', ');
      const originRegion = [fields.O, fields.A].filter(Boolean).join(', ');
      const sourceAlbum = [fields.D, fields.B].filter(Boolean).join(', ');

      const abcTags = [
        'ABC', // Always include ABC
        fields.R, // rhythm_style (e.g., "jig")
        fields.K && `key ${fields.K}`, // key (e.g., "key A")
        composerArranger, // composer/arranger (without URLs)
        originRegion, // origin/region
        sourceAlbum // source/album
      ].filter(Boolean);

      setTagsValue(abcTags.join(', '));

      // Auto-populate description with formatted ABC information
      const descriptionParts = [];
      if (fields.T) descriptionParts.push(`Title: ${fields.T}`);
      if (fields.R) descriptionParts.push(`Type: ${fields.R}`);
      if (fields.K) descriptionParts.push(`Key: ${fields.K}`);
      if (fields.C || fields.Z) {
        const composers = [fields.C, fields.Z].filter(Boolean).join(', ');
        descriptionParts.push(`Composer/Arranger: ${composers}`);
      }
      if (fields.O || fields.A) {
        const origins = [fields.O, fields.A].filter(Boolean).join(', ');
        descriptionParts.push(`Origin: ${origins}`);
      }
      if (fields.D || fields.B) {
        const sources = [fields.D, fields.B].filter(Boolean).join(', ');
        descriptionParts.push(`Source: ${sources}`);
      }

      const description = descriptionParts.join('\n');
      setDescriptionValue(description);

      showSuccess('ABC fields and description populated from file');
    } catch (error) {
      console.error('Error parsing ABC file:', error);
      showError('Failed to parse ABC file. Please check the file format.');
    }
  };

  // Handle file selection and read content
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      setUploadStoredFilename('');
      return;
    }

    // Check if it's an ABC file
    const fileName = file.name.toLowerCase();
    const isABCFile = fileName.endsWith('.abc') || fileName.endsWith('.txt') ||
      file.type === 'text/plain' || file.type === 'text/x-abc' ||
      file.type === 'application/x-abc';

    if (isABCFile && selectedCategory === 'ABC-Tune') {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          setAbcFileContent(content);
        };
        reader.onerror = () => {
          showError('Failed to read file');
        };
        reader.readAsText(file);
      } catch (error) {
        console.error('Error reading file:', error);
        showError('Failed to read file');
      }
    } else {
      setAbcFileContent(null);
    }

    setNewFile(file);
    setUploadStoredFilename(cleanAssetFilenameHint(file.name));
  };

  // Auto-populate tags from ABC fields
  const handleAutoPopulateTags = () => {
    if (selectedCategory !== 'ABC-Tune') return;

    const abcTags = [
      'ABC', // Always include ABC
      abcFields.rhythm_style,
      abcFields.key && `key ${abcFields.key}`,
      abcFields.composer_arranger,
      abcFields.origin_region,
      abcFields.source_album
    ].filter(Boolean);

    // Keep existing tags that aren't ABC-related (simple approach: just replace all tags)
    setTagsValue(abcTags.join(', '));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Duplicate checking functions
  const checkForDuplicates = async () => {
    try {
      setCheckingDuplicates(true);
      const response = await fetch('/api/assets/duplicates/check', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to check for duplicates');

      const data = await response.json();
      setDuplicateData(data);
      setActiveTab('duplicates');
      showSuccess('Duplicate check completed');
    } catch (error) {
      console.error('Error checking duplicates:', error);
      showError('Failed to check for duplicates');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleSmartSelectIdentical = () => {
    if (!duplicateData) return;

    const newSelection = new Set(selectedAssets);
    let selectCount = 0;

    // 1. Process duplicate file paths (Explicitly identical physical files)
    if (duplicateData.duplicates.filePaths) {
      duplicateData.duplicates.filePaths.forEach(group => {
        const ids = group.ids.split(',').map(id => parseInt(id.trim())).sort((a, b) => a - b);
        // Keep the smallest ID (oldest), select the rest
        for (let i = 1; i < ids.length; i++) {
          if (!newSelection.has(ids[i])) {
            newSelection.add(ids[i]);
            selectCount++;
          }
        }
      });
    }

    // 2. Process duplicate filenames
    if (duplicateData.duplicates.filenames) {
      duplicateData.duplicates.filenames.forEach(group => {
        const ids = group.ids.split(',').map(id => parseInt(id.trim())).sort((a, b) => a - b);
        const sizes = group.sizes ? group.sizes.split(',').map(s => s.trim()) : [];
        const paths = group.paths ? group.paths.split(',').map(p => p.trim()) : [];

        for (let i = 1; i < ids.length; i++) {
          // If both path and size match the first entry, it's an identical duplicate
          if (paths[i] === paths[0] && sizes[i] === sizes[0]) {
            if (!newSelection.has(ids[i])) {
              newSelection.add(ids[i]);
              selectCount++;
            }
          }
        }
      });
    }

    setSelectedAssets(newSelection);
    if (selectCount > 0) {
      showSuccess(`✨ Auto-selected ${selectCount} identical duplicate(s)`);
    } else {
      showSuccess('No additional identical duplicates found.');
    }
  };

  const handleAutoDeduplicate = async (dryRun = true) => {
    if (!dryRun && !window.confirm(
      `This will remap all database references to duplicate assets and deactivate ${
        (duplicateData && duplicateData.canonicalSummary && duplicateData.canonicalSummary.surplusRows) || 'surplus'
      } surplus asset rows.\n\nA dry run will show you the plan without making changes.\n\nProceed with APPLY (no dry run)?`
    )) return;

    setAutoDeduping(true);
    setAutoDedupeResult(null);
    try {
      const url = `/api/assets/duplicates/auto-deduplicate${dryRun ? '?dryRun=true' : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Auto-deduplicate failed');
      const result = await response.json();
      setAutoDedupeResult({ ...result, dryRun });
      if (!dryRun) {
        showSuccess(`✅ ${result.message}`);
        checkForDuplicates(); // refresh stats
      } else {
        showSuccess(`🔍 Dry run complete — ${result.duplicateGroups} groups, ${result.surplusRows} surplus rows`);
      }
    } catch (error) {
      console.error('Auto-deduplicate error:', error);
      showError('Auto-deduplicate failed');
    } finally {
      setAutoDeduping(false);
    }
  };

  const fetchInactiveAssets = async () => {
    try {
      const response = await fetch('/api/assets/duplicates/inactive', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch inactive assets');

      const data = await response.json();
      setInactiveAssets(data);
      setShowInactiveModal(true);
    } catch (error) {
      console.error('Error fetching inactive assets:', error);
      showError('Failed to fetch inactive assets');
    }
  };

  const markAsInactive = async (assetIds) => {
    if (!window.confirm(`Are you sure you want to mark ${assetIds.length} asset(s) as inactive?`)) return;

    try {
      const response = await fetch('/api/assets/duplicates/mark-inactive', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ assetIds })
      });

      if (!response.ok) throw new Error('Failed to mark assets as inactive');

      const result = await response.json();
      showSuccess(result.message);
      setSelectedAssets(new Set());

      // Refresh duplicate check if on duplicates tab
      if (activeTab === 'duplicates') {
        checkForDuplicates();
      } else {
        fetchAssets();
      }
    } catch (error) {
      console.error('Error marking assets as inactive:', error);
      showError('Failed to mark assets as inactive');
    }
  };

  const cleanupInactiveAssets = async () => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE all inactive assets? This cannot be undone!')) return;

    try {
      const response = await fetch(`/api/assets/duplicates/cleanup?deleteFiles=${deleteFilesOnCleanup}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to cleanup inactive assets');

      const result = await response.json();
      showSuccess(result.message);
      setShowInactiveModal(false);

      // Refresh data
      if (activeTab === 'duplicates') {
        checkForDuplicates();
      }
    } catch (error) {
      console.error('Error cleaning up inactive assets:', error);
      showError('Failed to cleanup inactive assets');
    }
  };

  const reactivateAssets = async (assetIds) => {
    try {
      const response = await fetch('/api/assets/duplicates/reactivate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ assetIds })
      });

      if (!response.ok) throw new Error('Failed to reactivate assets');

      const result = await response.json();
      showSuccess(result.message);
      fetchInactiveAssets();
    } catch (error) {
      console.error('Error reactivating assets:', error);
      showError('Failed to reactivate assets');
    }
  };

  const toggleAssetSelection = (assetId) => {
    const newSelection = new Set(selectedAssets);
    if (newSelection.has(assetId)) {
      newSelection.delete(assetId);
      setIsAllSelected(false); // Selection no longer represents "all"
    } else {
      newSelection.add(assetId);
    }
    setSelectedAssets(newSelection);
  };

  // Toggle select all assets on current page
  const toggleSelectAll = () => {
    if (selectedAssets.size === assets.length && assets.length > 0) {
      // Deselect all
      setSelectedAssets(new Set());
      setIsAllSelected(false);
    } else {
      // Select all on current page
      const allIds = assets.map(asset => asset.id);
      setSelectedAssets(new Set(allIds));
    }
  };

  const handleSelectAllMatched = () => {
    setIsAllSelected(true);
    // When isAllSelected is true, selectedAssets can be cleared or used to show local overrides
    // Usually for bulk actions, we check isAllSelected first.
    setSelectedAssets(new Set());
    showSuccess(`Selected all ${pagination.total} matching assets`);
  };

  // Export selected assets as ZIP
  const handleExportSelected = async () => {
    if (selectedAssets.size === 0) {
      showError('Please select at least one asset to export');
      return;
    }

    try {
      const body = {
        assetIds: Array.from(selectedAssets)
      };

      if (isAllSelected) {
        body.allMatching = true;
        body.filters = filters;
      }

      const response = await fetch('/api/assets/export/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export assets');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const filename = parseContentDispositionFilename(
        response.headers.get('Content-Disposition'),
        'assets_export.zip',
      );

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showSuccess(`Successfully exported ${isAllSelected ? pagination.total : selectedAssets.size} asset(s) as ZIP file!`);
      setSelectedAssets(new Set()); // Clear selection after export
      setIsAllSelected(false);
    } catch (error) {
      console.error('Error exporting selected assets:', error);
      showError('Failed to export assets. Please try again.');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return '🖼️';
      case 'music': return '🎵';
      case 'video': return '🎥';
      case 'document': return '📄';
      default: return '📁';
    }
  };

  // Import assets from ZIP
  const handleImportZipClick = () => {
    importZipFileInputRef.current?.click();
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      showError('Please select a valid ZIP file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('zipfile', file);

      const response = await fetch('/api/assets/import/bulk', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import failed');
      }

      const result = await response.json();
      showSuccess(`Import completed: ${result.imported} imported, ${result.updated} updated`);

      if (result.errors && result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
        showError(`Some assets had errors. Check console for details.`);
      }

      fetchAssets();
    } catch (error) {
      console.error('Error importing ZIP:', error);
      showError('Failed to import assets: ' + error.message);
    }

    // Reset file input
    e.target.value = '';
  };

  // Handle bulk ABC file import
  const handleBulkABCImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.abc') && !fileName.endsWith('.txt')) {
      showError('Please select an ABC or TXT file');
      return;
    }

    try {
      const fileText = await file.text();
      console.log('Parsing ABC file, length:', fileText.length);
      const { tunes, header } = parseMultipleTunes(fileText);

      console.log(`Parsed ${tunes.length} tune(s) from file`);
      console.log('Header length:', header.length);
      console.log('First tune:', tunes[0] ? { title: tunes[0].fields.T, fields: Object.keys(tunes[0].fields) } : 'none');

      if (tunes.length === 0) {
        showError('No tunes found in file. Make sure the file contains ABC notation with X: fields.');
        return;
      }

      const processedTunes = mapTunesToBulkImportEntries(tunes);

      // Check which tunes already exist
      try {
        const filenames = processedTunes.map(t => t.filename).join(',');
        const checkResponse = await fetch(`/api/assets/check-exists?filenames=${encodeURIComponent(filenames)}`, {
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
          }
        });

        if (checkResponse.ok) {
          const existingAssets = await checkResponse.json();

          // Mark tunes that already exist
          processedTunes.forEach(tune => {
            if (existingAssets[tune.filename]) {
              tune.exists = true;
              tune.existingAssetId = existingAssets[tune.filename].id;
            }
          });
        }
      } catch (error) {
        console.error('Error checking existing assets:', error);
        // Continue anyway - we'll just not mark existing ones
      }

      setBulkImportHeader(header);
      setBulkImportTunes(processedTunes);
      setShowBulkImportModal(true);
    } catch (error) {
      console.error('Error parsing ABC file:', error);
      showError('Failed to parse ABC file: ' + error.message);
    }

    // Reset file input
    e.target.value = '';
  };

  // Import a single tune from bulk import (opens upload form with pre-filled data)
  const handleImportTune = (tune) => {
    openUploadModalWithBulkTune(tune);
  };

  // Import all tunes from bulk import modal (upload all at once)
  const handleImportAllTunes = async () => {
    if (bulkImportTunes.length === 0) return;

    // Filter out tunes that already exist (unless user wants to update them)
    const tunesToImport = bulkImportTunes.filter(tune => !tune.exists);
    const existingTunes = bulkImportTunes.filter(tune => tune.exists);

    if (existingTunes.length > 0) {
      const skipExisting = window.confirm(
        `${existingTunes.length} tune(s) already exist and will be skipped. ` +
        `Do you want to continue importing the remaining ${tunesToImport.length} tune(s)?`
      );
      if (!skipExisting) {
        return;
      }
    }

    if (tunesToImport.length === 0) {
      showError('All tunes already exist. No new tunes to import.');
      return;
    }

    setBulkImportProgress({ current: 0, total: tunesToImport.length, status: 'Starting import...' });
    setUploading(true);

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < tunesToImport.length; i++) {
        const tune = tunesToImport[i];
        setBulkImportProgress({
          current: i + 1,
          total: tunesToImport.length,
          status: `Uploading: ${tune.title}...`
        });

        try {
          // Create a Blob from the full content (header + tune)
          const blob = new Blob([tune.fullContent], { type: 'text/plain' });
          const file = new File([blob], tune.filename, { type: 'text/plain' });

          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', 'ABC-Tune');
          formData.append('description', tune.description);
          formData.append('tags', tune.tags);
          formData.append('is_public', 'true');

          const response = await fetch('/api/assets', {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: formData
          });

          if (!response.ok) {
            let errorMessage = `Upload failed (HTTP ${response.status})`;
            try {
              const errorText = await response.text();
              try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.message || errorData.error || errorMessage;
                // If it's a duplicate (409), provide more helpful message
                if (response.status === 409 && errorData.existingId) {
                  errorMessage = `Duplicate detected: ${errorMessage}`;
                }
              } catch (e) {
                // Not JSON, use text as is
                errorMessage = errorText || errorMessage;
              }
            } catch (e) {
              errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(errorMessage);
          }

          const result = await response.json();
          console.log(`Successfully imported: ${tune.title} (ID: ${result.id})`);
          successCount++;
        } catch (error) {
          errorCount++;
          const errorMsg = error.message || 'Unknown error';
          errors.push({ tune: tune.title, error: errorMsg });
          console.error(`Error importing ${tune.title}:`, error);
          // Show error for this specific tune
          console.error(`Failed to import "${tune.title}": ${errorMsg}`);
        }
      }

      setBulkImportProgress({
        current: bulkImportTunes.length,
        total: bulkImportTunes.length,
        status: 'Import complete!'
      });

      if (errorCount > 0) {
        showError(`Import completed with ${errorCount} error(s). ${successCount} tune(s) imported successfully.`);
        console.error('Import errors:', errors);
      } else {
        showSuccess(`Successfully imported ${successCount} tune(s)!`);
      }

      // Close modal and refresh assets
      setShowBulkImportModal(false);
      setBulkImportTunes([]);
      setBulkImportHeader('');
      fetchAssets();
    } catch (error) {
      console.error('Error during bulk import:', error);
      showError('Bulk import failed: ' + error.message);
    } finally {
      setUploading(false);
      setBulkImportProgress({ current: 0, total: 0, status: '' });
    }
  };

  if (!canManageAssets) {
    return (
      <div className="admin-assets">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to manage assets.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-assets">
      <div className="admin-header">
        <h2>Asset Management</h2>
        <div className="header-actions">
          {/* Primary Actions */}
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            ⬆️ Upload
          </button>

          <button
            className="btn btn-primary"
            onClick={() => bulkImportFileInputRef.current?.click()}
            title="Import multiple ABC tunes from one file"
          >
            📚 Single/CD Import ABC
          </button>
          <input
            ref={bulkImportFileInputRef}
            type="file"
            accept=".abc,.txt"
            onChange={handleBulkABCImport}
            style={{ display: 'none' }}
          />
          <input
            ref={importFileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleCSVUpload}
            style={{ display: 'none' }}
          />
          <input
            ref={importZipFileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleZipUpload}
            style={{ display: 'none' }}
          />

          {selectedAssets.size > 0 && (
            <>
              <button
                className="btn btn-success"
                onClick={handleExportSelected}
              >
                📦 Export {isAllSelected ? `All (${pagination.total})` : `Selected (${selectedAssets.size})`}
              </button>
              <HelpPopup title="Export Assets as ZIP" position="bottom">
                <p><strong>Export selected assets</strong> to create a backup or transfer them to another environment.</p>

                <p><strong>What's included in the ZIP:</strong></p>
                <ul>
                  <li><code>manifest.json</code> - All asset metadata (filenames, descriptions, tags, categories)</li>
                  <li>All selected asset files in their original format</li>
                </ul>

                <div className="tip">
                  💡 <strong>Use Case:</strong> Perfect for backing up images, audio files, or migrating assets between dev and production.
                </div>

                <p><strong>To import later:</strong> Use the "📦 Import ZIP" button with the exported file.</p>
              </HelpPopup>
            </>
          )}

          {/* Import/Export Group */}
          <div className="btn-group">
            <button
              className="btn btn-sm btn-info"
              onClick={downloadCSV}
              title="Export all assets as CSV"
            >
              📥 Export CSV
            </button>
            <button
              className="btn btn-sm btn-info"
              onClick={handleImportClick}
              title="Import assets from CSV"
            >
              📤 Import CSV
            </button>
            <button
              className="btn btn-sm btn-success"
              onClick={handleImportZipClick}
              title="Import assets from ZIP (with files)"
            >
              📦 Import ZIP
            </button>
            <HelpPopup title="Import Assets from ZIP" position="bottom">
                <p><strong>Import assets from a ZIP file</strong> previously exported from this system.</p>

                <p><strong>What happens during import:</strong></p>
                <ul>
                  <li>Reads <code>manifest.json</code> for asset metadata</li>
                  <li>Creates or updates assets in the database</li>
                  <li>Uploads all asset files automatically</li>
                  <li>Preserves descriptions, tags, and categories</li>
                </ul>

                <div className="tip">
                  💡 <strong>Smart Handling:</strong> If an asset with the same filename exists, it will be updated instead of duplicated.
                </div>

                <p><strong>After import:</strong> You'll see a summary showing how many assets were imported/updated.</p>
              </HelpPopup>
          </div>

          {/* Tools Group */}
          <div className="btn-group">
            <button
              className="btn btn-sm btn-secondary"
              onClick={checkForDuplicates}
              disabled={checkingDuplicates}
              title="Check for duplicate assets"
            >
              {checkingDuplicates ? '⏳' : '🔍'} Duplicates
            </button>
            {duplicateData && duplicateData.inactive && duplicateData.inactive.count > 0 && (
              <button
                className="btn btn-sm btn-warning"
                onClick={fetchInactiveAssets}
                title="View inactive assets"
              >
                📋 Inactive ({duplicateData.inactive.count})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="asset-tabs">
        <button
          className={`tab-btn ${activeTab === 'assets' ? 'active' : ''}`}
          onClick={() => setActiveTab('assets')}
        >
          📁 Assets
        </button>
        <button
          className={`tab-btn ${activeTab === 'duplicates' ? 'active' : ''}`}
          onClick={() => {
            if (!duplicateData) {
              checkForDuplicates();
            } else {
              setActiveTab('duplicates');
            }
          }}
        >
          🔍 Duplicates
          {duplicateData && (
            <span className="badge">
              {(duplicateData.canonicalSummary && duplicateData.canonicalSummary.groups) || 0} dups
              {duplicateData.orphans && duplicateData.orphans.count > 0 && ` · ${duplicateData.orphans.count} orphans`}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'assets' ? (
        <>
          {/* Filters */}
          <div className="filters">
            {/* Select All Checkbox */}
            {assets.length > 0 && (
              <div className="filter-group-selection-wrapper">
                <div className="filter-group filter-group-select-all">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(selectedAssets.size === assets.length && assets.length > 0) || isAllSelected}
                      onChange={toggleSelectAll}
                    />
                    <span>Select Page ({assets.length})</span>
                  </label>
                </div>
                {!isAllSelected && selectedAssets.size === assets.length && pagination.total > assets.length && (
                  <button className="btn btn-sm btn-link select-all-btn" onClick={handleSelectAllMatched}>
                    Select all {pagination.total} matching assets
                  </button>
                )}
                {isAllSelected && (
                  <div className="selection-info-message">
                    All {pagination.total} assets matching current filters are selected.
                    <button className="btn btn-sm btn-link" onClick={() => { setIsAllSelected(false); setSelectedAssets(new Set()); }}>
                      Clear selection
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="filter-group">
              <select
                value={filters.file_type}
                onChange={(e) => handleFilterChange('file_type', e.target.value)}
              >
                <option value="">🗂️ All Types</option>
                <option value="image">🖼️ Images</option>
                <option value="music">🎵 Music</option>
                <option value="document">📄 Documents</option>
                <option value="video">🎬 Videos</option>
              </select>
            </div>

            <div className="filter-group">
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">📁 All Categories</option>
                {categories && categories.map ? categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                )) : []}
              </select>
            </div>

            <div className="filter-group">
              <select
                value={filters.file_size}
                onChange={(e) => handleFilterChange('file_size', e.target.value)}
              >
                <option value="">📏 All Sizes</option>
                <option value="small">🐭 Small (&lt; 1MB)</option>
                <option value="medium">📦 Medium (1-10MB)</option>
                <option value="large">📚 Large (10-50MB)</option>
                <option value="xlarge">🐘 XLarge (&gt; 50MB)</option>
              </select>
            </div>

            <div className="filter-group filter-group-search">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="🔍 Search by ID, name, tags..."
                title="Search by ID, filename, tags, description, category"
              />
            </div>

            <div className="filter-group">
              <label className="checkbox-label" title="Include assets that have been marked as inactive">
                <input
                  type="checkbox"
                  checked={filters.show_inactive}
                  onChange={(e) => handleFilterChange('show_inactive', e.target.checked)}
                />
                <span>🚫 Show Inactive</span>
              </label>
            </div>

            <div className="filter-group filter-group-limit">
              <label>Show:</label>
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(e.target.value)}
              >
                <option value="20">20</option>
                <option value="40">40</option>
                <option value="60">60</option>
                <option value="80">80</option>
                <option value="-1">All</option>
              </select>
            </div>
          </div>

          {/* Assets Grid */}
          <div className="assets-grid">
            {loading ? (
              <div className="loading">Loading assets...</div>
            ) : assets.length === 0 ? (
              <div className="no-assets">No assets found</div>
            ) : (
              assets.map(asset => (
                <div key={asset.id} className="asset-card">
                  {/* Selection Checkbox */}
                  <div className="asset-selection-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedAssets.has(asset.id)}
                      onChange={() => toggleAssetSelection(asset.id)}
                      title="Select asset for export"
                    />
                  </div>

                  <div className="asset-preview">
                    {asset.file_type === 'image' ? (
                      <img
                        src={`/api/assets/${asset.id}/thumbnail`}
                        alt={asset.alt_text || asset.original_filename}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : asset.file_type === 'music' ? (
                      <div className="file-icon music-icon">
                        🎵
                        <audio controls style={{ width: '100%', marginTop: '10px' }}>
                          <source src={`/api/assets/${asset.id}/thumbnail`} type={asset.mime_type} />
                        </audio>
                      </div>
                    ) : asset.file_type === 'video' ? (
                      <div className="file-icon video-icon">
                        🎥
                        <video controls style={{ width: '100%', marginTop: '10px', maxHeight: '200px' }}>
                          <source src={`/api/assets/${asset.id}/thumbnail`} type={asset.mime_type} />
                          Your browser does not support HTML video.
                        </video>
                      </div>
                    ) : (
                      <div className="file-icon">
                        {getFileIcon(asset.file_type)}
                      </div>
                    )}
                    <div className="file-icon-fallback" style={{ display: 'none' }}>
                      {getFileIcon(asset.file_type)}
                    </div>
                  </div>

                  <div className="asset-info">
                    <div className="asset-header">
                      <span className="asset-id" title="Asset ID">🆔 {asset.id}</span>
                      <span className="asset-filename" title={asset.original_filename}>📄 {asset.original_filename}</span>
                      <div className="asset-quick-links">
                        <a
                          className="asset-view-link"
                          href={`/api/assets/${asset.id}/thumbnail`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open thumbnail in new tab"
                        >
                          View
                        </a>
                      </div>
                      {asset.is_active === 0 && (
                        <span className="badge badge-error" style={{ marginLeft: '8px', fontSize: '10px' }}>
                          🚫 Inactive
                        </span>
                      )}
                    </div>
                    {asset.description && (
                      <p className="asset-description" title={asset.description}>{asset.description}</p>
                    )}
                    <div className="asset-meta">
                      <span title="File Size">📏 {formatFileSize(asset.file_size)}</span>
                      <span title="File Type">🗂️ {asset.file_type}</span>
                      {asset.category && <span title="Category">📁 {asset.category}</span>}
                      {asset.is_public ? (
                        <span title="Public">🌐 Public</span>
                      ) : (
                        <span title="Private">🔒 Private</span>
                      )}
                    </div>
                    {asset.file_path && (
                      <div className="asset-path" style={{
                        fontSize: '10px',
                        color: 'var(--color-text-secondary)',
                        marginTop: '5px',
                        wordBreak: 'break-all',
                        opacity: 0.7
                      }}>
                        📍{' '}
                        <span title={displayAssetFilePath(asset) !== String(asset.file_path) ? `DB: ${asset.file_path}` : asset.file_path}>
                          {displayAssetFilePath(asset)}
                        </span>
                      </div>
                    )}
                    {asset.tag_names && (
                      <div className="tags">
                        {asset.tag_names.split(',').map(tag => (
                          <span key={tag} className="tag">{tag.trim()}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="asset-actions">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => handleViewLink(asset)}
                      title="View and copy asset link"
                    >
                      🔗 View Link
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownload(asset)}
                    >
                      Download
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleEdit(asset)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(asset.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination?.pages > 1 && (
            <div className="pagination">
              <button
                disabled={pagination?.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span>Page {pagination?.page || 1} of {pagination?.pages || 1}</span>
              <button
                disabled={pagination?.page === pagination?.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}

          {/* Upload Modal */}
          {showUploadModal && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>
                    Upload Asset
                    <HelpPopup title="Asset Upload Guide" position="bottom">
                      <p><strong>Assets</strong> are reusable files that can be referenced across your website - images, audio files, PDFs, and more.</p>

                      <div className="tip">
                        💡 <strong>Tip:</strong> Use descriptive filenames and tags to make assets easy to find later!
                      </div>

                      <p><strong>Supported File Types:</strong></p>
                      <ul>
                        <li><strong>Images:</strong> JPG, PNG, GIF, WebP</li>
                        <li><strong>Audio:</strong> MP3, WAV, OGG</li>
                        <li><strong>Video:</strong> MP4, WebM</li>
                        <li><strong>Documents:</strong> PDF, DOC, DOCX, TXT</li>
                      </ul>

                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Use categories to organize related assets</li>
                        <li>Add descriptive alt text for images (SEO & accessibility)</li>
                        <li>Tag assets with keywords for easy searching</li>
                        <li>Mark assets as public if they should be visible to all users</li>
                      </ul>
                    </HelpPopup>
                  </h3>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadStoredFilename('');
                      setSelectedCategory('');
                      setAbcFields({
                        rhythm_style: '',
                        key: '',
                        composer_arranger: '',
                        origin_region: '',
                        source_album: '',
                        tune_title: ''
                      });
                      setTagsValue('');
                      setDescriptionValue('');
                      setAbcFileContent(null);
                      setSelectedProductIds([]);
                      setProductIdsInput('');
                      setSelectedProducts([]);
                    }}
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleUpload} className="upload-form">
                  <div className="form-group">
                    <label>File:</label>
                    {newFile ? (
                      <div style={{
                        padding: '10px',
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: '6px',
                        border: '1px solid var(--color-border)',
                        marginBottom: '10px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>📄 {newFile.name} ({formatFileSize(newFile.size)})</span>
                          <button
                            type="button"
                            onClick={() => {
                              setNewFile(null);
                              setUploadStoredFilename('');
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: 'var(--color-secondary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      </div>
                    ) : null}
                    <input
                      type="file"
                      name="file"
                      ref={fileInputRef}
                      required={!newFile}
                      accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.abc,.mid,.midi"
                      onChange={handleFileChange}
                    />
                    {selectedCategory === 'ABC-Tune' && abcFileContent && (
                      <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary)' }}>
                        ✓ ABC file loaded - Click "Auto-populate from File" button above to fill fields
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="upload-stored-filename">Stored filename (optional)</label>
                    <input
                      id="upload-stored-filename"
                      type="text"
                      value={uploadStoredFilename}
                      onChange={(e) => setUploadStoredFilename(e.target.value)}
                      placeholder="e.g. cape-breton-book-cover.png"
                      autoComplete="off"
                    />
                    <small className="form-text" style={{ display: 'block', marginTop: '6px' }}>
                      If set, this name appears in the public path under <code>/assets/...</code> (subfolder still follows file type). Leave blank for an auto-generated name. Use this to replace awkward names such as macOS screenshots with special spaces.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>
                      Category:
                      <HelpPopup title="Asset Categories" position="right">
                        <p><strong>Categories</strong> help organize your assets into logical groups.</p>
                        <div className="example-box">
                          <strong>Examples:</strong><br />
                          • Product Images<br />
                          • Tune Notation<br />
                          • Audio Samples<br />
                          • Documentation
                        </div>
                        <p>Click <strong>➕ New</strong> to create a new category if needed.</p>
                      </HelpPopup>
                    </label>
                    <div className="category-input-group">
                      <select
                        name="category"
                        value={selectedCategory}
                        onChange={(e) => {
                          setSelectedCategory(e.target.value);
                          if (e.target.value !== 'ABC-Tune') {
                            // Reset ABC fields if category changes
                            setAbcFields({
                              rhythm_style: '',
                              key: '',
                              composer_arranger: '',
                              origin_region: '',
                              source_album: '',
                              tune_title: ''
                            });
                            setAbcFileContent(null);
                          } else if (e.target.value === 'ABC-Tune' && newFile) {
                            // If switching to ABC-Tune and file is already selected, try to read it
                            const fileName = newFile.name.toLowerCase();
                            const isABCFile = fileName.endsWith('.abc') || fileName.endsWith('.txt') ||
                              newFile.type === 'text/plain' || newFile.type === 'text/x-abc' ||
                              newFile.type === 'application/x-abc';
                            if (isABCFile) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setAbcFileContent(event.target.result);
                              };
                              reader.readAsText(newFile);
                            }
                          }
                        }}
                      >
                        <option value="">Select Category</option>
                        {categories && categories.map ? categories.map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        )) : []}
                      </select>
                      <button
                        type="button"
                        className="btn-add-category"
                        onClick={() => setShowCategoryModal(true)}
                        title="Create new category"
                      >
                        ➕ New
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Subcategory:</label>
                    <input type="text" name="subcategory" />
                  </div>

                  {/* ABC-Tune Specific Fields */}
                  {selectedCategory === 'ABC-Tune' && (
                    <div className="abc-fields-section" style={{
                      border: '2px solid var(--color-primary)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px',
                      backgroundColor: 'var(--color-surface)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, color: 'var(--color-primary)' }}>
                          🎵 ABC Tune Metadata
                        </h4>
                        {abcFileContent && (
                          <button
                            type="button"
                            onClick={handleAutoPopulateFromFile}
                            style={{
                              padding: '6px 16px',
                              fontSize: '13px',
                              backgroundColor: 'var(--color-primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                            title="Auto-populate all fields from ABC file"
                          >
                            ✨ Auto-populate from File
                          </button>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Rhythm/Style (R:):</label>
                        <input
                          type="text"
                          name="abc_rhythm_style"
                          value={abcFields.rhythm_style}
                          onChange={(e) => setAbcFields({ ...abcFields, rhythm_style: e.target.value })}
                          placeholder="e.g., jig, reel, strathspey"
                        />
                      </div>

                      <div className="form-group">
                        <label>Key (K:):</label>
                        <input
                          type="text"
                          name="abc_key"
                          value={abcFields.key}
                          onChange={(e) => setAbcFields({ ...abcFields, key: e.target.value })}
                          placeholder="e.g., A-major, G-minor"
                        />
                      </div>

                      <div className="form-group">
                        <label>Composer/Arranger (C: / Z:):</label>
                        <input
                          type="text"
                          name="abc_composer_arranger"
                          value={abcFields.composer_arranger}
                          onChange={(e) => setAbcFields({ ...abcFields, composer_arranger: e.target.value })}
                          placeholder="e.g., Kinnon Beaton, Traditional"
                        />
                      </div>

                      <div className="form-group">
                        <label>Origin/Region (O: / A:):</label>
                        <input
                          type="text"
                          name="abc_origin_region"
                          value={abcFields.origin_region}
                          onChange={(e) => setAbcFields({ ...abcFields, origin_region: e.target.value })}
                          placeholder="e.g., Cape Breton, Scottish, Irish"
                        />
                      </div>

                      <div className="form-group">
                        <label>Source/Album (D: / B:):</label>
                        <input
                          type="text"
                          name="abc_source_album"
                          value={abcFields.source_album}
                          onChange={(e) => setAbcFields({ ...abcFields, source_album: e.target.value })}
                          placeholder="e.g., The Tap Session, The Beaton Collection"
                        />
                      </div>

                      <div className="form-group">
                        <label>Tune Title (T:):</label>
                        <input
                          type="text"
                          name="abc_tune_title"
                          value={abcFields.tune_title}
                          onChange={(e) => setAbcFields({ ...abcFields, tune_title: e.target.value })}
                          placeholder="e.g., Big Mike's Jig"
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      name="description"
                      rows="3"
                      value={descriptionValue}
                      onChange={(e) => setDescriptionValue(e.target.value)}
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label>
                      Alt Text (for images):
                      <HelpPopup title="Alt Text Importance" position="right">
                        <p><strong>Alt text</strong> describes the image content for:</p>
                        <ul>
                          <li><strong>Accessibility:</strong> Screen readers for visually impaired users</li>
                          <li><strong>SEO:</strong> Search engines index this text</li>
                          <li><strong>Fallback:</strong> Displays if image fails to load</li>
                        </ul>
                        <div className="example-box">
                          <strong>Good example:</strong><br />
                          "Winston Fitzgerald playing fiddle at Cape Breton festival"
                        </div>
                        <div className="warning">
                          ⚠️ Be descriptive and specific, not generic like "image" or "photo"
                        </div>
                      </HelpPopup>
                    </label>
                    <input type="text" name="alt_text" />
                  </div>
                  <div className="form-group">
                    <label>
                      Tags (comma-separated):
                      {selectedCategory === 'ABC-Tune' && (
                        <button
                          type="button"
                          onClick={handleAutoPopulateTags}
                          style={{
                            marginLeft: '10px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            backgroundColor: 'var(--color-primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Auto-populate tags from ABC fields"
                        >
                          ✨ Auto-populate
                        </button>
                      )}
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={tagsValue}
                      onChange={(e) => setTagsValue(e.target.value)}
                      placeholder="e.g., cover, sample, traditional"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Related Products:
                      <HelpPopup title="Related Products" position="right">
                        <p>Select products that are related to this asset. This will create a bidirectional relationship - the asset will appear on product pages and products will appear on asset pages.</p>
                      </HelpPopup>
                    </label>
                    <select
                      multiple
                      value={selectedProductIds.map(String)}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        setSelectedProductIds(selected);
                      }}
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '8px',
                        border: '2px solid var(--color-border)',
                        borderRadius: '6px',
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} {product.sku ? `(${product.sku})` : ''}
                        </option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary)' }}>
                      Hold Ctrl/Cmd to select multiple products
                    </small>
                  </div>
                  <div className="form-group">
                    <label>
                      <input type="checkbox" name="is_public" defaultChecked />
                      Public (visible to all users)
                    </label>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowUploadModal(false);
                        setSelectedCategory('');
                        setAbcFields({
                          rhythm_style: '',
                          key: '',
                          composer_arranger: '',
                          origin_region: '',
                          source_album: '',
                          tune_title: ''
                        });
                        setTagsValue('');
                        setDescriptionValue('');
                        setAbcFileContent(null);
                        setSelectedProductIds([]);
                        setProductIdsInput('');
                        setSelectedProducts([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Bulk Import Modal */}
          {showBulkImportModal && (
            <div className="modal-overlay">
              <div className="modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
                <div className="modal-header">
                  <h3>Bulk Import ABC Tunes</h3>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowBulkImportModal(false);
                      setBulkImportTunes([]);
                      setBulkImportHeader('');
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  {bulkImportHeader && (
                    <div style={{
                      marginBottom: '20px',
                      padding: '10px',
                      backgroundColor: 'var(--color-surface)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '150px',
                      overflow: 'auto'
                    }}>
                      <strong>File Header:</strong>
                      <pre style={{ margin: '5px 0 0 0', fontSize: '11px' }}>{bulkImportHeader}</pre>
                    </div>
                  )}

                  <div style={{ marginBottom: '15px' }}>
                    <strong>Found {bulkImportTunes.length} tune(s) ready to import:</strong>
                  </div>

                  <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    {bulkImportTunes.map((tune, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '12px',
                          marginBottom: '10px',
                          backgroundColor: tune.exists ? 'var(--color-surface)' : 'var(--color-background)',
                          border: `2px solid ${tune.exists ? 'var(--color-secondary)' : 'var(--color-border)'}`,
                          borderRadius: '6px',
                          opacity: tune.exists ? 0.7 : 1
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <strong style={{ color: 'var(--color-primary)' }}>
                              {tune.title}
                            </strong>
                            {tune.exists && (
                              <span style={{
                                marginLeft: '10px',
                                padding: '2px 8px',
                                backgroundColor: 'var(--color-secondary)',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '11px'
                              }}>
                                Already Exists
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleImportTune(tune)}
                            className="btn btn-sm btn-primary"
                            disabled={tune.exists}
                            style={{ marginLeft: '10px' }}
                          >
                            Import
                          </button>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '5px' }}>
                          <div><strong>Type:</strong> {tune.metadata.rhythm_style || 'N/A'}</div>
                          <div><strong>Key:</strong> {tune.metadata.key_signature || tune.metadata.key || 'N/A'}</div>
                          <div><strong>Composer:</strong> {tune.metadata.composer_arranger || 'N/A'}</div>
                          <div><strong>Filename:</strong> {tune.filename}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: '6px',
                    border: '2px solid var(--color-primary)'
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Import All Tunes</strong>
                    </div>
                    {bulkImportProgress.total > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{
                          width: '100%',
                          height: '20px',
                          backgroundColor: 'var(--color-border)',
                          borderRadius: '10px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${(bulkImportProgress.current / bulkImportProgress.total) * 100}%`,
                            height: '100%',
                            backgroundColor: 'var(--color-primary)',
                            transition: 'width 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {bulkImportProgress.current} / {bulkImportProgress.total}
                          </div>
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                          {bulkImportProgress.status}
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleImportAllTunes}
                      className="btn btn-primary"
                      disabled={uploading || bulkImportTunes.filter(t => !t.exists).length === 0}
                      style={{ width: '100%' }}
                    >
                      {uploading ? 'Importing...' : `Import All (${bulkImportTunes.filter(t => !t.exists).length} tunes)`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Modal */}
          {editingAsset && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Edit Asset #{editingAsset.id}</h3>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setEditingAsset(null);
                      setNewFile(null);
                      setSelectedCategory('');
                      setAbcFields({
                        rhythm_style: '',
                        key: '',
                        composer_arranger: '',
                        origin_region: '',
                        source_album: '',
                        tune_title: ''
                      });
                      setTagsValue('');
                      setDescriptionValue('');
                      setAbcFileContent(null);
                    }}
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleUpdateAsset} className="edit-form">
                  {/* Asset Preview */}
                  <div className="form-group edit-preview">
                    <label>Preview:</label>
                    <div className="asset-preview-large">
                      {editingAsset.file_type === 'image' ? (
                        <img
                          src={`/api/assets/${editingAsset.id}/thumbnail`}
                          alt={editingAsset.alt_text || editingAsset.original_filename}
                          style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                        />
                      ) : editingAsset.file_type === 'music' ? (
                        <div className="music-preview">
                          <div className="file-icon" style={{ fontSize: '48px', marginBottom: '10px' }}>🎵</div>
                          <audio controls style={{ width: '100%' }}>
                            <source src={`/api/assets/${editingAsset.id}/thumbnail`} type={editingAsset.mime_type} />
                          </audio>
                        </div>
                      ) : editingAsset.file_type === 'video' ? (
                        <div className="video-preview">
                          <div className="file-icon" style={{ fontSize: '48px', marginBottom: '10px' }}>🎥</div>
                          <video controls style={{ width: '100%', maxHeight: '400px' }}>
                            <source src={`/api/assets/${editingAsset.id}/thumbnail`} type={editingAsset.mime_type} />
                            Your browser does not support HTML video.
                          </video>
                        </div>
                      ) : (
                        <div className="file-icon" style={{ fontSize: '64px' }}>
                          {getFileIcon(editingAsset.file_type)}
                        </div>
                      )}
                    </div>
                    <p className="file-info">
                      <strong>Asset ID:</strong> {editingAsset.id} | <strong>File:</strong> {editingAsset.original_filename} ({formatFileSize(editingAsset.file_size)})
                    </p>
                    {editingAsset.file_path && (
                      <p className="file-path-info" style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '5px' }}>
                        <strong>Path:</strong>{' '}
                        <span title={displayAssetFilePath(editingAsset) !== String(editingAsset.file_path) ? `DB: ${editingAsset.file_path}` : editingAsset.file_path}>
                          {displayAssetFilePath(editingAsset)}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-stored-filename">Stored filename (URL / on disk)</label>
                    <input
                      id="edit-stored-filename"
                      type="text"
                      value={editStoredFilename}
                      onChange={(e) => setEditStoredFilename(e.target.value)}
                      placeholder="filename-in-folder.png"
                      autoComplete="off"
                    />
                    <small className="form-text">
                      For assets under <code>/assets/...</code> only. Change this and save to rename the file on the server (the folder part of the path stays the same). Do not include <code>/</code> or <code>..</code>. If another file already uses the name, a suffix may be added.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Replace File (optional):</label>
                    <input
                      type="file"
                      name="file"
                      accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.abc,.mid,.midi"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setNewFile(file);
                        // If ABC file and category is ABC-Tune, read it
                        if (file && selectedCategory === 'ABC-Tune') {
                          const fileName = file.name.toLowerCase();
                          const isABCFile = fileName.endsWith('.abc') || fileName.endsWith('.txt');
                          if (isABCFile) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setAbcFileContent(event.target.result);
                            };
                            reader.readAsText(file);
                          }
                        }
                      }}
                    />
                    <small className="form-text">
                      Leave empty to keep current file. Upload a new file to replace the existing one.
                    </small>
                    {selectedCategory === 'ABC-Tune' && abcFileContent && (
                      <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary)' }}>
                        ✓ ABC file loaded - Click "Auto-populate from File" button above to fill fields
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Category:</label>
                    <select
                      name="category"
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        if (e.target.value !== 'ABC-Tune') {
                          // Reset ABC fields if category changes
                          setAbcFields({
                            rhythm_style: '',
                            key: '',
                            composer_arranger: '',
                            origin_region: '',
                            source_album: '',
                            tune_title: ''
                          });
                          setAbcFileContent(null);
                        } else if (e.target.value === 'ABC-Tune' && newFile) {
                          // If switching to ABC-Tune and file is already selected, try to read it
                          const fileName = newFile.name.toLowerCase();
                          const isABCFile = fileName.endsWith('.abc') || fileName.endsWith('.txt') ||
                            newFile.type === 'text/plain' || newFile.type === 'text/x-abc' ||
                            newFile.type === 'application/x-abc';
                          if (isABCFile) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setAbcFileContent(event.target.result);
                            };
                            reader.readAsText(newFile);
                          }
                        }
                      }}
                    >
                      <option value="">Select Category</option>
                      {categories && categories.map ? categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      )) : []}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subcategory:</label>
                    <input
                      type="text"
                      name="subcategory"
                      defaultValue={editingAsset.subcategory || ''}
                    />
                  </div>

                  {/* ABC-Tune Specific Fields */}
                  {selectedCategory === 'ABC-Tune' && (
                    <div className="abc-fields-section" style={{
                      border: '2px solid var(--color-primary)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px',
                      backgroundColor: 'var(--color-surface)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, color: 'var(--color-primary)' }}>
                          🎵 ABC Tune Metadata
                        </h4>
                        {abcFileContent && (
                          <button
                            type="button"
                            onClick={handleAutoPopulateFromFile}
                            style={{
                              padding: '6px 16px',
                              fontSize: '13px',
                              backgroundColor: 'var(--color-primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                            title="Auto-populate all fields from ABC file"
                          >
                            ✨ Auto-populate from File
                          </button>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Rhythm/Style (R:):</label>
                        <input
                          type="text"
                          name="abc_rhythm_style"
                          value={abcFields.rhythm_style}
                          onChange={(e) => setAbcFields({ ...abcFields, rhythm_style: e.target.value })}
                          placeholder="e.g., jig, reel, strathspey"
                        />
                      </div>

                      <div className="form-group">
                        <label>Key (K:):</label>
                        <input
                          type="text"
                          name="abc_key"
                          value={abcFields.key}
                          onChange={(e) => setAbcFields({ ...abcFields, key: e.target.value })}
                          placeholder="e.g., A-major, G-minor"
                        />
                      </div>

                      <div className="form-group">
                        <label>Composer/Arranger (C: / Z:):</label>
                        <input
                          type="text"
                          name="abc_composer_arranger"
                          value={abcFields.composer_arranger}
                          onChange={(e) => setAbcFields({ ...abcFields, composer_arranger: e.target.value })}
                          placeholder="e.g., Kinnon Beaton, Traditional"
                        />
                      </div>

                      <div className="form-group">
                        <label>Origin/Region (O: / A:):</label>
                        <input
                          type="text"
                          name="abc_origin_region"
                          value={abcFields.origin_region}
                          onChange={(e) => setAbcFields({ ...abcFields, origin_region: e.target.value })}
                          placeholder="e.g., Cape Breton, Scottish, Irish"
                        />
                      </div>

                      <div className="form-group">
                        <label>Source/Album (D: / B:):</label>
                        <input
                          type="text"
                          name="abc_source_album"
                          value={abcFields.source_album}
                          onChange={(e) => setAbcFields({ ...abcFields, source_album: e.target.value })}
                          placeholder="e.g., The Tap Session, The Beaton Collection"
                        />
                      </div>

                      <div className="form-group">
                        <label>Tune Title (T:):</label>
                        <input
                          type="text"
                          name="abc_tune_title"
                          value={abcFields.tune_title}
                          onChange={(e) => setAbcFields({ ...abcFields, tune_title: e.target.value })}
                          placeholder="e.g., Big Mike's Jig"
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      name="description"
                      rows="3"
                      value={descriptionValue}
                      onChange={(e) => setDescriptionValue(e.target.value)}
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label>Alt Text (for images):</label>
                    <input
                      type="text"
                      name="alt_text"
                      defaultValue={editingAsset.alt_text || ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Tags (comma-separated):
                      {selectedCategory === 'ABC-Tune' && (
                        <button
                          type="button"
                          onClick={handleAutoPopulateTags}
                          style={{
                            marginLeft: '10px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            backgroundColor: 'var(--color-primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Auto-populate tags from ABC fields"
                        >
                          ✨ Auto-populate
                        </button>
                      )}
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={tagsValue}
                      onChange={(e) => setTagsValue(e.target.value)}
                      placeholder="e.g., cover, sample, traditional"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Product ID (for tune/product relationship):
                      <HelpPopup title="Related Products" position="right">
                        <p>Enter Product IDs (comma-separated) or use "Browse Products" to search and select. This creates a bidirectional relationship - the asset will appear on product pages and products will appear on asset pages.</p>
                      </HelpPopup>
                    </label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        value={productIdsInput}
                        onChange={(e) => handleProductIdsChange(e.target.value)}
                        placeholder="e.g., 624"
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '2px solid var(--color-border)',
                          borderRadius: '6px',
                          backgroundColor: 'var(--color-surface)',
                          color: 'var(--color-text-primary)'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowProductPicker(true)}
                        className="btn btn-secondary"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        🔍 Browse Products
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary)' }}>
                      💡 Enter the Product ID or click "Browse Products" to search and select a product.
                    </small>
                    {selectedProducts.length > 0 && (
                      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--color-surface)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--color-text-secondary)' }}>
                          Selected Products:
                        </div>
                        {selectedProducts.map(product => (
                          <div key={product.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px',
                            marginBottom: '4px',
                            backgroundColor: 'var(--color-background)',
                            borderRadius: '4px'
                          }}>
                            <span style={{ fontSize: '13px' }}>
                              <strong>{product.name}</strong> {product.sku ? `(${product.sku})` : ''} - ID: {product.id}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeProduct(product.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                fontSize: '18px',
                                padding: '0 8px'
                              }}
                              title="Remove product"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="is_public"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                      />
                      Public (visible to all users)
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      Active
                    </label>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setEditingAsset(null);
                        setNewFile(null);
                        setSelectedCategory('');
                        setAbcFields({
                          rhythm_style: '',
                          key: '',
                          composer_arranger: '',
                          origin_region: '',
                          source_album: '',
                          tune_title: ''
                        });
                        setTagsValue('');
                        setAbcFileContent(null);
                        setSelectedProductIds([]);
                        setProductIdsInput('');
                        setSelectedProducts([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Duplicates Tab */
        <div className="duplicates-view">
          {!duplicateData ? (
            <div className="no-data">
              <p>No duplicate check has been performed yet.</p>
              <button className="btn btn-primary" onClick={checkForDuplicates} disabled={checkingDuplicates}>
                {checkingDuplicates ? 'Checking...' : 'Check for Duplicates'}
              </button>
            </div>
          ) : (
            <>
              {/* Single consolidated dashboard (overview + duplicate signals + breakdowns) */}
              {duplicateData.statistics && (
                <div className="duplicates-dashboard">
                  <h3 className="duplicates-dashboard-title">📊 Asset &amp; duplicate dashboard</h3>

                  <h4 className="dashboard-section-title">Library overview</h4>
                  <div className="stats-grid stats-grid--dashboard">
                    <div className="stat-card">
                      <div className="stat-value">{duplicateData.statistics.total_assets || 0}</div>
                      <div className="stat-label">Total Active Assets</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{formatFileSize(duplicateData.statistics.total_size || 0)}</div>
                      <div className="stat-label">Total Size</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{duplicateData.statistics.unique_original_filenames || 0}</div>
                      <div className="stat-label">Unique Original Filenames</div>
                    </div>
                    <div className={`stat-card ${duplicateData.inactive && duplicateData.inactive.count > 0 ? 'stat-card--muted' : 'stat-card--ok'}`}>
                      <div className="stat-value">{(duplicateData.inactive && duplicateData.inactive.count) || 0}</div>
                      <div className="stat-label">Inactive Assets</div>
                    </div>
                  </div>

                  <h4 className="dashboard-section-title">Duplicate signals</h4>
                  <div className="stats-grid stats-grid--dashboard">
                    {(() => {
                      const groups = duplicateData.duplicates.originalFilenames.length;
                      const surplus = (duplicateData.canonicalSummary && duplicateData.canonicalSummary.surplusRows) || 0;
                      return (
                        <div className={`stat-card ${groups === 0 ? 'stat-card--ok' : 'stat-card--warn'}`}>
                          <div className="stat-value">{groups}</div>
                          <div className="stat-label">Duplicate Groups</div>
                          {groups > 0 && (
                            <div className="stat-sublabel">{surplus} surplus rows · Auto-Deduplicate below</div>
                          )}
                          {groups === 0 && <div className="stat-sublabel">✓ No duplicate groups by original filename</div>}
                        </div>
                      );
                    })()}
                    {(() => {
                      const v = duplicateData.duplicates.filePaths.length;
                      return (
                        <div className={`stat-card ${v === 0 ? 'stat-card--ok' : 'stat-card--warn'}`}>
                          <div className="stat-value">{v}</div>
                          <div className="stat-label">Duplicate File Paths</div>
                          {v > 0 && <div className="stat-sublabel">Multiple DB rows → same file</div>}
                          {v === 0 && <div className="stat-sublabel">✓ All paths unique</div>}
                        </div>
                      );
                    })()}
                    {(() => {
                      const v = duplicateData.duplicates.filenames.length;
                      return (
                        <div className={`stat-card ${v === 0 ? 'stat-card--ok' : 'stat-card--warn'}`}>
                          <div className="stat-value">{v}</div>
                          <div className="stat-label">Duplicate Filenames</div>
                          {v > 0 && <div className="stat-sublabel">Same system-generated name</div>}
                          {v === 0 && <div className="stat-sublabel">✓ All filenames unique</div>}
                        </div>
                      );
                    })()}
                    {(() => {
                      const v = duplicateData.duplicates.sizeAndMime.length;
                      return (
                        <div className={`stat-card ${v === 0 ? 'stat-card--ok' : 'stat-card--warn'}`}>
                          <div className="stat-value">{v}</div>
                          <div className="stat-label">Content Duplicates</div>
                          {v > 0 && <div className="stat-sublabel">Same name + size + MIME</div>}
                          {v === 0 && <div className="stat-sublabel">✓ None in sample</div>}
                        </div>
                      );
                    })()}
                  </div>

                  <h4 className="dashboard-section-title">Reference scan (orphan detection)</h4>
                  {duplicateData.referenceScan && duplicateData.referenceScan.sources && (
                    <div className="reference-scan-panel">
                      <p className="reference-scan-meta">
                        <strong>{duplicateData.referenceScan.distinctReferencedIds ?? '—'}</strong> distinct asset
                        IDs matched in SQLite (union of all sources below).
                      </p>
                      {duplicateData.referenceScan.exclusionsNote && (
                        <p className="reference-scan-exclusions">{duplicateData.referenceScan.exclusionsNote}</p>
                      )}
                      <div className="reference-scan-table-wrap">
                        <table className="reference-scan-table">
                          <thead>
                            <tr>
                              <th>Source</th>
                              <th>Rows</th>
                              <th>Cells scanned</th>
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {duplicateData.referenceScan.sources.map((s) => (
                              <tr key={s.key}>
                                <td><code className="reference-scan-code">{s.key}</code></td>
                                <td>{s.rowCount}</td>
                                <td>{s.cellsScanned != null ? s.cellsScanned : '—'}</td>
                                <td>{s.note || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <h4 className="dashboard-section-title">Orphans (no hit in scan above)</h4>
                  <div className="stats-grid stats-grid--dashboard stats-grid--narrow">
                    <div className={`stat-card ${(duplicateData.orphans && duplicateData.orphans.count) > 0 ? 'stat-card--info' : 'stat-card--ok'}`}>
                      <div className="stat-value">{(duplicateData.orphans && duplicateData.orphans.count) || 0}</div>
                      <div className="stat-label">Orphaned Assets</div>
                      {duplicateData.orphans && duplicateData.orphans.note && (
                        <div className="stat-sublabel" title={duplicateData.orphans.note}>
                          SQLite pages, products, templates, etc.
                        </div>
                      )}
                    </div>
                    <div className="stat-card stat-card--muted">
                      <div className="stat-value">{formatFileSize((duplicateData.orphans && duplicateData.orphans.total_size) || 0)}</div>
                      <div className="stat-label">Orphaned Size</div>
                    </div>
                  </div>

                  <div className="dashboard-split">
                    {duplicateData.orphans && duplicateData.orphans.byType && duplicateData.orphans.byType.length > 0 ? (
                      <div className="type-breakdown type-breakdown--orphans type-breakdown--panel">
                        <h4>🗂️ Orphaned by type</h4>
                        <p className="breakdown-note">{duplicateData.orphans.note}</p>
                        {duplicateData.orphans.byType.map(t => (
                          <div key={t.file_type} className="type-item">
                            <span className="type-name">{t.file_type || 'Unknown'}</span>
                            <span className="type-count type-count--orphan">{t.count} files</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="type-breakdown type-breakdown--panel type-breakdown--empty">
                        <h4>🗂️ Orphaned by type</h4>
                        <p className="breakdown-note">No orphans in this bucket.</p>
                      </div>
                    )}

                    {duplicateData.statistics.typeBreakdown && duplicateData.statistics.typeBreakdown.length > 0 ? (
                      <div className="type-breakdown type-breakdown--panel">
                        <h4>📁 All active assets by type</h4>
                        <p className="breakdown-note">Full library mix (every active asset).</p>
                        {duplicateData.statistics.typeBreakdown.map(type => (
                          <div key={type.file_type} className="type-item">
                            <span className="type-name">{type.file_type || 'Unknown'}</span>
                            <span className="type-count">{type.count} files ({formatFileSize(type.total_size)})</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/* Auto-Deduplicate panel */}
                  {duplicateData.canonicalSummary && duplicateData.canonicalSummary.groups > 0 && (
                    <div className="auto-dedup-panel">
                      <h4>🔁 Auto-Deduplicate</h4>
                      <p>
                        Remaps all references to the canonical (lowest) ID per group, then soft-deletes the
                        {' '}<strong>{duplicateData.canonicalSummary.surplusRows}</strong> surplus rows.
                        Safe: uses soft-delete only and mirrors the <code>deduplicate-assets.js</code> script.
                      </p>
                      <div className="auto-dedup-actions">
                      <button
                        className="btn btn-info"
                        onClick={() => handleAutoDeduplicate(true)}
                        disabled={autoDeduping}
                      >
                        {autoDeduping ? '⏳ Running…' : '🔍 Dry Run (preview)'}
                      </button>
                      <button
                        className="btn btn-warning"
                        onClick={() => handleAutoDeduplicate(false)}
                        disabled={autoDeduping}
                      >
                        {autoDeduping ? '⏳ Running…' : '⚡ Apply Deduplication'}
                      </button>
                    </div>

                    {/* Dry-run / apply result */}
                    {autoDedupeResult && (
                      <div className={`auto-dedup-result ${autoDedupeResult.dryRun ? 'auto-dedup-result--dryrun' : 'auto-dedup-result--applied'}`}>
                        <strong>{autoDedupeResult.dryRun ? '🔍 Dry Run Result' : '✅ Applied'}</strong>
                        <ul>
                          <li>Duplicate groups: <strong>{autoDedupeResult.duplicateGroups}</strong></li>
                          <li>Surplus rows {autoDedupeResult.dryRun ? 'to deactivate' : 'deactivated'}: <strong>{autoDedupeResult.surplusRows}</strong></li>
                          <li>Tune references remapped: <strong>{autoDedupeResult.remapped && autoDedupeResult.remapped.tunes}</strong></li>
                          <li>Content page patches: <strong>{autoDedupeResult.remapped && autoDedupeResult.remapped.contentPages}</strong></li>
                          {!autoDedupeResult.dryRun && autoDedupeResult.remainingDuplicateGroups === 0 && (
                            <li className="auto-dedup-clean">✅ Verified: no duplicate groups remain</li>
                          )}
                        </ul>
                        {autoDedupeResult.remapLog && autoDedupeResult.remapLog.length > 0 && (
                          <details className="auto-dedup-log">
                            <summary>Remap log ({autoDedupeResult.remapLog.length} entries)</summary>
                            <table>
                              <thead><tr><th>Original filename</th><th>From ID</th><th>→ To ID</th></tr></thead>
                              <tbody>
                                {autoDedupeResult.remapLog.map((r, i) => (
                                  <tr key={i}><td>{r.name}</td><td>{r.from}</td><td>{r.to}</td></tr>
                                ))}
                              </tbody>
                            </table>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </div>
              )}

              {/* Check if there are any duplicates */}
              {duplicateData.duplicates.filenames.length === 0 &&
                duplicateData.duplicates.originalFilenames.length === 0 &&
                duplicateData.duplicates.filePaths.length === 0 &&
                duplicateData.duplicates.sizeAndMime.length === 0 ? (
                <div className="no-data">
                  <p>✅ No duplicate signals in the lists below (paths, names, or content sample).</p>
                  {(duplicateData.orphans && duplicateData.orphans.count > 0) && (
                    <p className="no-data-hint">You still have orphaned assets — see the dashboard above.</p>
                  )}
                  <button className="btn btn-primary" onClick={checkForDuplicates} disabled={checkingDuplicates}>
                    {checkingDuplicates ? 'Checking...' : 'Refresh Check'}
                  </button>
                </div>
              ) : (
                <>
                  {/* Duplicate Filenames */}
                  {duplicateData.duplicates.filenames.length > 0 && (
                    <div className="duplicate-section">
                      <h3>📄 Duplicate Filenames ({duplicateData.duplicates.filenames.length})</h3>
                      <p className="section-note">Assets with the same system-generated filename</p>
                      {duplicateData.duplicates.filenames.map((dup, idx) => (
                        <div key={idx} className="duplicate-group">
                          <div className="duplicate-header">
                            <strong>{dup.filename}</strong>
                            <span className="badge">{dup.count} copies</span>
                          </div>
                          <div className="duplicate-items">
                            {dup.ids.split(',').map((id, i) => {
                              const assetId = parseInt(id.trim());
                              const paths = dup.paths ? dup.paths.split(',') : [];
                              const sizes = dup.sizes ? dup.sizes.split(',') : [];
                              return (
                                <div key={i} className="duplicate-item">
                                  <input
                                    type="checkbox"
                                    checked={selectedAssets.has(assetId)}
                                    onChange={() => toggleAssetSelection(assetId)}
                                  />
                                  <div className="item-details">
                                    <div><strong>ID:</strong> {assetId}</div>
                                    {paths[i] && <div><strong>Path:</strong> {paths[i]}</div>}
                                    {sizes[i] && <div><strong>Size:</strong> {formatFileSize(parseInt(sizes[i]))}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Duplicate Original Filenames — canonical vs surplus */}
                  {duplicateData.duplicates.originalFilenames.length > 0 && (
                    <div className="duplicate-section">
                      <h3>📝 Duplicate Original Filenames ({duplicateData.duplicates.originalFilenames.length} groups)</h3>
                      <p className="section-note">
                        Grouped by original filename. The <span className="badge-canonical">canonical</span> row (lowest ID) is kept;
                        <span className="badge-surplus"> surplus</span> rows are safe to deactivate via Auto-Deduplicate above.
                      </p>
                      {duplicateData.duplicates.originalFilenames.map((dup, idx) => {
                        const canonicalId = dup.canonical_id;
                        return (
                          <div key={idx} className="duplicate-group">
                            <div className="duplicate-header">
                              <strong>{dup.original_filename}</strong>
                              <span className="badge">{dup.count} copies</span>
                              <span className="badge badge-canonical-small">canonical: #{canonicalId}</span>
                            </div>
                            <div className="duplicate-items">
                              {dup.ids.split(',').map((id, i) => {
                                const assetId = parseInt(id.trim());
                                const isCanonical = assetId === canonicalId;
                                const filenames = dup.filenames ? dup.filenames.split(',') : [];
                                const paths = dup.paths ? dup.paths.split(',') : [];
                                const sizes = dup.sizes ? dup.sizes.split(',') : [];
                                return (
                                  <div key={i} className={`duplicate-item ${isCanonical ? 'duplicate-item--canonical' : 'duplicate-item--surplus'}`}>
                                    {!isCanonical && (
                                      <input
                                        type="checkbox"
                                        checked={selectedAssets.has(assetId)}
                                        onChange={() => toggleAssetSelection(assetId)}
                                      />
                                    )}
                                    {isCanonical && <span className="canonical-placeholder" />}
                                    <div className="item-details">
                                      <div>
                                        <strong>ID:</strong> {assetId}
                                        {isCanonical
                                          ? <span className="badge-canonical"> ✓ canonical</span>
                                          : <span className="badge-surplus"> surplus</span>
                                        }
                                      </div>
                                      {filenames[i] && <div><strong>Filename:</strong> {filenames[i]}</div>}
                                      {paths[i] && <div><strong>Path:</strong> {paths[i]}</div>}
                                      {sizes[i] && <div><strong>Size:</strong> {formatFileSize(parseInt(sizes[i]))}</div>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Duplicate File Paths */}
                  {duplicateData.duplicates.filePaths.length > 0 && (
                    <div className="duplicate-section">
                      <h3>📂 Duplicate File Paths ({duplicateData.duplicates.filePaths.length})</h3>
                      <p className="section-note">Multiple database entries pointing to the same file path</p>
                      {duplicateData.duplicates.filePaths.map((dup, idx) => (
                        <div key={idx} className="duplicate-group">
                          <div className="duplicate-header">
                            <strong>{dup.file_path}</strong>
                            <span className="badge">{dup.count} copies</span>
                          </div>
                          <div className="duplicate-items">
                            {dup.ids.split(',').map((id, i) => {
                              const assetId = parseInt(id.trim());
                              const filenames = dup.filenames ? dup.filenames.split(',') : [];
                              return (
                                <div key={i} className="duplicate-item">
                                  <input
                                    type="checkbox"
                                    checked={selectedAssets.has(assetId)}
                                    onChange={() => toggleAssetSelection(assetId)}
                                  />
                                  <div className="item-details">
                                    <div><strong>ID:</strong> {assetId}</div>
                                    {filenames[i] && <div><strong>Filename:</strong> {filenames[i]}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Duplicate Size & MIME */}
                  {duplicateData.duplicates.sizeAndMime.length > 0 && (
                    <div className="duplicate-section">
                      <h3>🔍 Potential Duplicates ({duplicateData.duplicates.sizeAndMime.length})</h3>
                      <p className="section-note" style={{
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        marginBottom: '15px'
                      }}>
                        ⚠️ <strong>Note:</strong> These are files with the <strong>same filename</strong>, size, and type.
                        Different files with the same size are NOT shown here. For true duplicate detection,
                        file content hashing would be required.
                      </p>
                      {duplicateData.duplicates.sizeAndMime.map((dup, idx) => (
                        <div key={idx} className="duplicate-group">
                          <div className="duplicate-header">
                            <strong>{dup.original_filename || 'Unknown'} - {formatFileSize(dup.file_size)} - {dup.mime_type}</strong>
                            <span className="badge">{dup.count} copies</span>
                          </div>
                          <div className="duplicate-items">
                            {dup.ids.split(',').map((id, i) => {
                              const assetId = parseInt(id.trim());
                              const filenames = dup.filenames ? dup.filenames.split(',') : [];
                              const originalFilenames = dup.original_filenames ? dup.original_filenames.split(',') : [];
                              return (
                                <div key={i} className="duplicate-item">
                                  <input
                                    type="checkbox"
                                    checked={selectedAssets.has(assetId)}
                                    onChange={() => toggleAssetSelection(assetId)}
                                  />
                                  <div className="item-details">
                                    <div><strong>ID:</strong> {assetId}</div>
                                    {filenames[i] && <div><strong>Filename:</strong> {filenames[i]}</div>}
                                    {originalFilenames[i] && <div><strong>Original:</strong> {originalFilenames[i]}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="duplicates-actions" style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-warning" onClick={checkForDuplicates} disabled={checkingDuplicates}>
                      {checkingDuplicates ? '⏳ Checking...' : '🔄 Refresh Check'}
                    </button>
                    <button
                      className="btn btn-info"
                      onClick={handleSmartSelectIdentical}
                      title="Automatically select duplicate records that point to the same physical file"
                    >
                      ✨ Smart Select Identical
                    </button>
                    {selectedAssets.size > 0 && (
                      <button
                        className="btn btn-danger"
                        onClick={() => markAsInactive(Array.from(selectedAssets))}
                      >
                        ⚠️ Mark Selected as Inactive ({selectedAssets.size})
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Category Creation Modal */}
      {showCategoryModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Create New Category</h3>
              <button
                className="btn-close"
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategoryName('');
                  setNewCategoryDescription('');
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateCategory} className="category-form">
              <div className="form-group">
                <label>Category Name: *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Videos, Product Images, Audio Samples"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description (optional):</label>
                <textarea
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  placeholder="Brief description of this category..."
                  rows="3"
                ></textarea>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCategoryModal(false);
                    setNewCategoryName('');
                    setNewCategoryDescription('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inactive Assets Modal */}
      {showInactiveModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>Inactive Assets Library</h3>
              <button className="btn-close" onClick={() => setShowInactiveModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                <p><strong>Note:</strong> Inactive assets are not visible on the main site or in standard asset lists. You can reactivate them or permanently delete them below.</p>
              </div>

              {inactiveAssets.length === 0 ? (
                <div className="no-data">No inactive assets found.</div>
              ) : (
                <>
                  <div className="inactive-summary" style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--color-surface)', borderRadius: '8px' }}>
                    <strong>Total Inactive:</strong> {inactiveAssets.length} assets
                  </div>

                  <div className="inactive-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', maxHeight: '400px', overflowY: 'auto', padding: '10px', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                    {inactiveAssets.map(asset => (
                      <div key={asset.id} className="inactive-item" style={{ padding: '10px', backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>ID: {asset.id}</div>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{asset.original_filename}</div>
                        <div style={{ fontSize: '12px' }}>{formatFileSize(asset.file_size)}</div>
                        <button
                          className="btn btn-sm btn-outline-success"
                          style={{ marginTop: '10px', width: '100%' }}
                          onClick={() => reactivateAssets([asset.id])}
                        >
                          Reactivate
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="modal-footer-actions" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '15px', padding: '15px', borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
                        <strong>⚠️ Danger Zone:</strong> Permanent deletion cannot be undone.
                      </div>
                      <button className="btn btn-danger" onClick={cleanupInactiveAssets}>
                        Permanently Delete All {inactiveAssets.length} Assets
                      </button>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: 'var(--color-background)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                        <input
                          type="checkbox"
                          checked={deleteFilesOnCleanup}
                          onChange={(e) => setDeleteFilesOnCleanup(e.target.checked)}
                        />
                        <span><strong>Delete physical files from disk</strong> (Safety check engaged: will only delete if not used by any active asset)</span>
                      </label>
                      <p style={{ margin: '5px 0 0 25px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                        💡 <strong>Default (Unchecked):</strong> Only the database record is removed. The file remains on the server for safety.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Picker Modal */}
      <ProductPickerModal
        isOpen={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onSelect={handleProductPickerSelect}
      />
    </div>
  );
};

export default AdminAssets;
